import {
  ANALYTICS_EVENTS,
  COMMON_PLOT_CONFIG,
  HIPHEN_GREEN,
  NULL_GROUP_LABEL,
  THEME,
} from "../../../constants";
import { slideExportConfig, traitLabel } from "./slideExport";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "reactstrap";
import { Chart } from "./Chart";
import { FloatingCard } from "../../../components/floatingCard/FloatingCard";
import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import { PropertyName } from "../../../components/PropertyName/PropertyName";
import { RandomSelectButton } from "../randomSelect/RandomSelectButton";
import { CfSelect as Select } from "../../../components/CfSelect";
import { addFocusedVarieties } from "../../../actions/resultMap";
import { selectFilteredAggregatedFeatures } from "../../../selectors/resultMap";
import { truncateNumber } from "../../../services/utils";
import { useAttributeColor } from "../../../hooks/useAttributeColor";
import { useClickAway } from "react-use";
import { useGetGroupColor } from "../../../hooks/useGetGroupColor";
import { useGetPropertyCategory } from "../../../hooks/useGetPropertyCategory";
import { useTracking } from "../../../analytics";

const PLOT_STYLE = { width: "100%", height: "100%" };

// "" is the modality of the plots without one, so "all" needs its own value.
const ALL_MODALITIES = { value: null, label: "All modalities" };

const STATS = [
  { value: "mean", label: "Mean" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];

// Which end of the ranking sits at the top of the chart.
const DIRECTIONS = [
  {
    value: "highest",
    label: "Highest first",
    icon: "fa-arrow-down-wide-short",
  },
  { value: "lowest", label: "Lowest first", icon: "fa-arrow-up-short-wide" },
];

// The value a group is drawn and ranked at: the picked statistic of its plots.
const statValue = (values, stat) =>
  stat === "mean" ? values.sum / values.count : values[stat];

export const GenotypeAnalyzer = ({ trait, exportName }) => {
  const [modalities, favoriteVarieties] = useSelector(({ resultMap }) => [
    resultMap.distinctModalities,
    resultMap.favoriteVarieties,
  ]);

  const aggregatedFeatures = useSelector(selectFilteredAggregatedFeatures);

  const [rankModality, setRankModality] = useState(ALL_MODALITIES);
  const [rankStat, setRankStat] = useState(STATS[0]);
  const [rankDirection, setRankDirection] = useState(DIRECTIONS[0]);
  const [rankOpen, setRankOpen] = useState(false);
  // Click-away on the button and the card together: on the card alone, the
  // mousedown on the button closed it and the click reopened it.
  const rankRef = useRef(null);
  useClickAway(rankRef, () => setRankOpen(false));
  const modalityOptions = [
    ALL_MODALITIES,
    ...modalities.map((modality) => ({
      value: modality ?? "",
      label: modality || NULL_GROUP_LABEL,
    })),
  ];
  // A modality gone with a new date or filter falls back to all of them.
  const rankedModality = modalityOptions.some(
    ({ value }) => value === rankModality.value
  )
    ? rankModality
    : ALL_MODALITIES;
  const rankLabel =
    rankedModality.value === null
      ? rankStat.label
      : `${rankStat.label} · ${rankedModality.label}`;

  // One row per genotype, ordered on the picked statistic of its plots in
  // the picked modality, or in all of them: Plotly's own order follows the
  // traces, so a genotype was placed by the first modality it appears in.
  // Genotypes without a value there sit at the bottom, in both directions.
  // Plotly draws the first category at the bottom.
  const genotypeOrder = useMemo(() => {
    const scores = {};
    aggregatedFeatures.forEach(({ genotype, modality, properties }) => {
      const values = properties[trait.technical_name];
      if (!values) return;
      scores[genotype] ??= null;
      if (
        rankedModality.value !== null &&
        (modality ?? "") !== rankedModality.value
      )
        return;
      const score = (scores[genotype] ??= {
        sum: 0,
        count: 0,
        min: Infinity,
        max: -Infinity,
      });
      score.sum += values.sum;
      score.count += values.count;
      score.min = Math.min(score.min, values.min);
      score.max = Math.max(score.max, values.max);
    });

    const genotypes = Object.keys(scores);
    const ranked = genotypes
      .filter((genotype) => scores[genotype])
      .sort(
        (a, b) =>
          statValue(scores[a], rankStat.value) -
          statValue(scores[b], rankStat.value)
      );
    if (rankDirection.value === "lowest") ranked.reverse();

    return [...genotypes.filter((genotype) => !scores[genotype]), ...ranked];
  }, [
    aggregatedFeatures,
    rankedModality,
    rankStat,
    rankDirection,
    trait.technical_name,
  ]);

  const getColor = useGetGroupColor();
  const getModalityColor = useAttributeColor("modality");

  const [displayData, longestGenotypeCharLength] = useMemo(() => {
    const modPreparedData = {};
    // This is used to give an approximation on yAxis margin to avoid using automargin
    let longestGenotypeCharLength = 0;

    modalities.forEach((modality) => {
      modality = modality ?? "";
      modPreparedData[modality] = {
        name: modality,
        meta: modality === "" ? NULL_GROUP_LABEL : modality,
        x: [],
        y: [],
        customdata: [],
        error_x: {
          symmetric: false,
          arrayminus: [],
          array: [],
          width: 2,
          thickness: 1.5,
        },
        mode: "markers",
        // All three statistics whatever the ranking: the ranked one alone
        // lost the spread the old Variation line gave.
        hovertemplate:
          "<b>Genotype: %{y}</b><extra></extra><br>Mean: %{customdata[0]}<br>Min: %{customdata[1]}<br>Max: %{customdata[2]}<br>Modality: %{meta}",
        marker: {
          opacity: 0.7,
          size: [],
          symbol: [],
          line: {
            color: [],
            width: 2,
          },
        },
        type: modalities.length > 1 ? "scatter" : "scattergl",
      };
    });

    // The rows are ordered by the y axis (genotypeOrder), not by this loop.
    aggregatedFeatures.forEach(
      ({ group, modality, properties, genotype, isControl }) => {
        const traitProperties = properties[trait.technical_name];
        if (!traitProperties) return;

        // Drawn at the ranked statistic, the bar still spans min to max: a
        // genotype ranked on its min is drawn at its min, not at its mean.
        const x = statValue(traitProperties, rankStat.value);
        modPreparedData[modality].x.push(truncateNumber(x));
        modPreparedData[modality].y.push(genotype);
        modPreparedData[modality].customdata.push(
          STATS.map(({ value }) =>
            truncateNumber(statValue(traitProperties, value))
          )
        );
        modPreparedData[modality].error_x.arrayminus.push(
          truncateNumber(x - traitProperties.min)
        );
        modPreparedData[modality].error_x.array.push(
          truncateNumber(traitProperties.max - x)
        );
        modPreparedData[modality].marker.line.color.push(getColor(group));
        modPreparedData[modality].marker.symbol.push(
          isControl
            ? "star-diamond-dot"
            : favoriteVarieties.includes(group)
            ? "star"
            : "diamond"
        );
        modPreparedData[modality].marker.size.push(isControl ? 16 : 6);

        longestGenotypeCharLength = Math.max(
          longestGenotypeCharLength,
          genotype.length
        );
      }
    );

    return [Object.values(modPreparedData), longestGenotypeCharLength];
  }, [
    aggregatedFeatures,
    favoriteVarieties,
    getColor,
    modalities,
    rankStat,
    trait.technical_name,
  ]);

  const getPropertyCategory = useGetPropertyCategory();

  const layout = useMemo(
    () => ({
      autosize: true,
      // Keeps what the user did on the chart across redraws (each selection
      // recolours the focused varieties, so redraws): the zoom until the trait
      // changes, the modalities hidden or isolated from the legend until the
      // modalities themselves change. `false` reset both on every selection.
      uirevision: trait.technical_name,
      dragmode: "select",
      // ~0.57em per character of the 14px font
      margin: { r: 15, l: 10 + longestGenotypeCharLength * 8, t: 0, b: 30 },
      plot_bgcolor: "transparent",
      paper_bgcolor: "white",
      yaxis: {
        gridcolor: THEME.indicators,
        tickfont: { size: 11.5 },
        type: "category",
        categoryorder: "array",
        categoryarray: genotypeOrder,
      },
      xaxis: {
        gridwidth: 1,
        gridcolor: THEME.indicators,
        tickfont: { size: 11.5 },
      },
      font: { color: THEME.indicators, size: 13 },
      legend: {
        y: 0.9,
        font: { size: 11.5 },
        uirevision: modalities.join("|"),
      },
      colorway:
        modalities.length > 1
          ? modalities.map((modality) =>
              modality ? getModalityColor(modality) : HIPHEN_GREEN
            )
          : [getPropertyCategory(trait.technical_name).color],
    }),
    [
      genotypeOrder,
      getModalityColor,
      getPropertyCategory,
      longestGenotypeCharLength,
      modalities,
      trait.technical_name,
    ]
  );

  const config = useMemo(
    () => ({
      scrollZoom: true,
      ...COMMON_PLOT_CONFIG,
      ...slideExportConfig({
        title: `Genotypes performance on ${traitLabel(trait)}`,
        filename: exportName,
      }),
    }),
    [exportName, trait]
  );

  const dispatch = useDispatch();

  const { trackEvent } = useTracking();

  // Points picked with the box (onSelected) or one by one with a click
  // (onClick): both add their varieties to the focused ones.
  const focusPoints = useCallback(
    (event) => {
      // A click also clears the box: Plotly then reports a selection
      // without points.
      if (!event?.points?.length) return;
      trackEvent(ANALYTICS_EVENTS.SELECT_FROM_GENOTYPE_ANALYZER);
      const groups = event.points.map((point) => {
        return `${point.y} ${point.data.name}`.trim();
      });
      dispatch(addFocusedVarieties(groups));
    },
    [dispatch, trackEvent]
  );

  return (
    <Chart
      icon="fa-solid fa-list-ol"
      title={
        trait.technical_name && (
          <span>
            Genotypes performance on{" "}
            <PropertyName
              className="breeder-selected-trait"
              showIcon
              showUnit
              technicalName={trait.technical_name}
            />
          </span>
        )
      }
      topRightContent={
        <>
          {/* One button in the header, the selects in a floating card: next
              to a long trait name the header has no room for them. */}
          <div className="genotype-rank" ref={rankRef}>
            <Button
              size="sm"
              color="link"
              className="genotype-rank-btn"
              title={`Rank by: ${rankLabel}, ${rankDirection.label.toLowerCase()}`}
              onClick={() => setRankOpen((open) => !open)}
            >
              <i className={`fa-solid ${rankDirection.icon} me-1`} />
              Rank: {rankLabel}
            </Button>
            {rankOpen && (
              <FloatingCard>
                <span className="genotype-rank-title">Rank genotypes by</span>
                {/* menuPosition fixed: the chart card clips an absolute menu */}
                {modalities.length > 1 && (
                  <Select
                    aria-label="Rank by modality"
                    isSearchable={false}
                    menuPosition="fixed"
                    options={modalityOptions}
                    value={rankedModality}
                    onChange={setRankModality}
                  />
                )}
                <Select
                  aria-label="Rank by statistic"
                  isSearchable={false}
                  menuPosition="fixed"
                  options={STATS}
                  value={rankStat}
                  onChange={setRankStat}
                />
                <Select
                  aria-label="Rank direction"
                  isSearchable={false}
                  menuPosition="fixed"
                  options={DIRECTIONS}
                  value={rankDirection}
                  onChange={setRankDirection}
                />
              </FloatingCard>
            )}
          </div>
          <RandomSelectButton key={aggregatedFeatures} />
        </>
      }
      missingData={aggregatedFeatures.length === 0 || !trait}
    >
      <Plot
        className="genotype-analyzer-plot"
        data={displayData}
        layout={layout}
        onSelected={focusPoints}
        onClick={focusPoints}
        config={config}
        style={PLOT_STYLE}
        useResizeHandler
      />
    </Chart>
  );
};

GenotypeAnalyzer.propTypes = {
  trait: PropTypes.object.isRequired,
  exportName: PropTypes.string.isRequired,
};
